import { Grid, styled } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import ProductCatalogCategorySelector from './Browse/ProductCatalogCategorySelector';
import ProductCatalogList from './ProductCatalogList';
import NotFoundPage from '../Common/NotFoundPage';
import useProductCatalogCategories from './hooks/useProductCatalogCategories';
import ProductCatalogProvider from './ProductCatalogProvider';
import useSelectedProductCatalogCollection from './hooks/useSelectedProductCatalogCollection';
import { useAppDispatch } from '../Common/_hooks/useAppDispatch';
import { useAppSelector } from '../Common/_hooks/useAppSelector';
import { FetchUserRewards } from '../UserData/_actions/UserRewardActions';

const ProductCatalogLayout = styled(Grid)({
	backgroundColor: '#f0f0f0',
});

const ProductCatalogResultsSection = styled(Grid)({
	position: 'relative',
	borderRight: 0,

	'@media (min-width:1600px)': {
		maxWidth: '86.333334%',
		flexBasis: '86.333334%',
	},
	'@media (min-width: 1200px) and (max-width: 1600px)': {
		maxWidth: '83.333334%',
		flexBasis: '83.333334%',
	},
	'@media(max-width:1199px)': {
		order: 3,
		borderRight: 'none',
	},
	'@media(max-width:600px)': {
		order: 3,
		borderLeft: 'none',
	},
});

const CategorySelectorSection = styled(Grid)({
	backgroundColor: '#fbfbfb',
	borderRight: '1px solid rgba(0, 0, 0, 0.08)',

	'@media (min-width:1600px)': {
		maxWidth: '13.666667%',
		flexBasis: '13.666667%',
	},

	'@media(min-width:601px)': {
		position: 'sticky',
		top: '52px',
		height: 'calc(100dvh - 52px)',
		overflowY: 'auto',
	},

	'@media(min-width:900px)': {
		top: '64px',
		height: 'calc(100dvh - 64px)',
	},

	'@media(max-width:1199px)': {
		order: 2,
	},
	'@media(max-width:600px)': {
		order: 1,
		display: 'none',
	},
});

export default function ProductCatalog() {
	const dispatch = useAppDispatch();
	const params = useParams<{ category1?: string, category2?: string }>();
	const isLoggedIn = useAppSelector(state => state.get('userData').get('loggedIn'));
	const {
		childCategoriesByParentIdAndUrlSlug,
		topLevelCategoriesByUrlSlug,
	} = useProductCatalogCategories();
	const selectedCollection = useSelectedProductCatalogCollection();

	const categoryNotFound = useMemo(() => {
		if (params.category1 === undefined) {
			return false;
		}

		if (selectedCollection !== undefined) {
			return false;
		}

		const category1 = topLevelCategoriesByUrlSlug.get(params.category1);
		if (category1 === undefined) {
			return true;
		}

		if (params.category2 === undefined) {
			return false;
		}

		return childCategoriesByParentIdAndUrlSlug
			.get(category1.get('id'))
			?.has(params.category2) !== true;
	}, [childCategoriesByParentIdAndUrlSlug, params.category1, params.category2, selectedCollection, topLevelCategoriesByUrlSlug])

	useEffect(() => {
		if (isLoggedIn && !categoryNotFound) {
			dispatch(FetchUserRewards());
		}
	}, [categoryNotFound, dispatch, isLoggedIn]);

	if (categoryNotFound) {
		return <NotFoundPage />
	}

	return <ProductCatalogProvider>
		<ProductCatalogLayout container>
			<CategorySelectorSection item xs={12} sm={4} md={3} lg={2}>
				<ProductCatalogCategorySelector />
			</CategorySelectorSection>
			<ProductCatalogResultsSection
				item
				xs={12} sm={8} md={9} lg={10}
				sx={{
					px: {
						xs: 2,
						md: 3,
					},
					py: {
						xs: 2,
						md: 4,
					},
				}}
			>
				<ProductCatalogList />
			</ProductCatalogResultsSection>
		</ProductCatalogLayout>
	</ProductCatalogProvider>
}
